// src/components/MuralItemDetailsPopup.js
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getAuth } from 'firebase/auth';
import Button from './Button/Button';
import H3 from './H3/H3';
import AuthorDetails from './AuthorDetails';
import closeBtnImage from '../assets/btn-close.png';
import shareIcon from '../assets/ico-url-perfil.svg';
import iconMoradia from '../assets/ico-azul-moradia.svg';
import iconAlimentacao from '../assets/ico-azul-alimentacao.svg';
import iconTrocas from '../assets/ico-azul-trocas.svg';
import iconTransporte from '../assets/ico-azul-transporte.svg';
import iconDocumentacao from '../assets/ico-azul-documentacao.svg';
import iconEmprego from '../assets/ico-azul-emprego.svg';
import iconComunicacao from '../assets/ico-azul-comunicacao.svg';
import iconTreinamento from '../assets/ico-azul-treinamento.svg';
import iconSaude from '../assets/ico-azul-saude.svg';
import iconOutros from '../assets/ico-azul-outros.svg';
import heartIconOver from '../assets/ico-coracao-over.svg';
import heartIcon from '../assets/ico-favorito.svg';
import { formatExpiry, isExpired } from '../utils/muralExpiry';

const categoryIcons = {
  moradia: iconMoradia,
  alimentacao: iconAlimentacao,
  trocas: iconTrocas,
  transporte: iconTransporte,
  documentacao: iconDocumentacao,
  emprego: iconEmprego,
  comunicacao: iconComunicacao,
  treinamento: iconTreinamento,
  saude: iconSaude,
  outros: iconOutros
};

// Mapa de categorias com acentuação
const categoryNamesWithAccent = {
  moradia: 'Moradia',
  alimentacao: 'Alimentação',
  trocas: 'Trocas',
  transporte: 'Transporte',
  documentacao: 'Documentação',
  emprego: 'Emprego',
  comunicacao: 'Comunicação',
  treinamento: 'Treinamento',
  saude: 'Saúde',
  outros: 'Outros',
};

const MuralItemDetailsPopup = ({ item, onClose, onSendMessage, authorInfo, onToggleFavorite }) => {
  const { t } = useTranslation('mural');
  const auth = getAuth();
  const currentUser = auth.currentUser;
  
  const isDemand = item.type === 'demand';
  const isCurrentUser = currentUser && item.userId === currentUser.uid;
  const categoryIcon = categoryIcons[item.category];
  const [isHeartHovered, setIsHeartHovered] = useState(false);
  const [shareFeedback, setShareFeedback] = useState('');

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/mural-da-rede/${item.id}`;
    const shareData = {
      title: item.title || 'Mural da Rede Refugia',
      text: `${isDemand ? 'Demanda' : 'Oferta'}: ${item.title || ''}`,
      url: shareUrl,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
        return;
      }
    } catch (err) {
      // usuário cancelou ou navegador bloqueou; cai no fallback
    }
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        setShareFeedback('Link copiado!');
      } else {
        window.prompt('Copie o link para compartilhar:', shareUrl);
      }
    } catch (err) {
      window.prompt('Copie o link para compartilhar:', shareUrl);
    }
    setTimeout(() => setShareFeedback(''), 2500);
  };

  // Format the date to DD/MM/YYYY
  const formatDate = (date) => {
    if (date) {
      const d = new Date(date.seconds * 1000);
      return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`;
    }
    return t('details.dateUnavailable');
  };

  return (
    <div className="fixed inset-0 z-[9998] bg-yellow-300 flex justify-center items-center">
      <div className="relative bg-white rounded-lg p-8 flex flex-col w-5/6 h-[90%] md:mt-0 md:w-[800px] md:h-[540px]">
        <button
          className="absolute z-[9999] top-[-1rem] right-[-1rem] w-8 h-8 bg-transparent border-none cursor-pointer p-0"
          onClick={onClose}
        >
          <img src={closeBtnImage} alt="Close" />
        </button>

        <div className="flex-1 overflow-y-auto mb-5">
          <div className="flex justify-between items-start">
            <div className={`px-3 py-3 text-white font-bold ${isDemand ? 'bg-pink-500' : 'bg-green-500'} rounded-tr-2xl rounded-br-2xl rounded-bl-2xl`}>
              {isDemand ? "Demanda" : "Oferta"}
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleShare}
                className="flex items-center text-sm text-blue-700 font-bold hover:underline"
                title="Compartilhar"
              >
                <img src={shareIcon} alt="" className="w-5 h-5 mr-1" />
                Compartilhar
              </button>
              {!isCurrentUser && (
                <button
                  className="w-6 h-6 flex items-center justify-center"
                  onMouseEnter={() => setIsHeartHovered(true)}
                  onMouseLeave={() => setIsHeartHovered(false)}
                  onClick={() => onToggleFavorite(item.id)}
                >
                  <img
                    src={isHeartHovered ? heartIconOver : (item.isFavorite ? heartIconOver : heartIcon)}
                    alt="Favoritar"
                    className="w-6 h-6"
                  />
                </button>
              )}
            </div>
          </div>
          {shareFeedback && (
            <p className="text-xs text-green-700 mt-1">{shareFeedback}</p>
          )}

          <div className="flex justify-between items-start mt-5">
            {categoryIcon && (
              <div className="flex items-center">
                <img src={categoryIcon} alt={categoryNamesWithAccent[item.category]} className="w-7 h-7 rounded-full" />
                <span className="text-blue-800 font-bold ml-2 capitalize">{categoryNamesWithAccent[item.category]}</span>
              </div>
            )}
          </div>

          <div className="flex flex-col md:flex-row w-full mt-5">
            <div className="w-full md:w-4/5 pr-2">
              <div className="flex flex-col md:flex-row justify-between items-baseline mb-2">
                <div className="w-full md:w-3/5">
                  <H3 text={item.title} textColor="purple" />
                </div>
                <div className="w-full md:w-2/5 text-sm text-gray-500 flex items-center">
                  <span>{formatDate(item.createdAt)}</span>
                </div>
              </div>
              <p className="font-normal text-base text-gray-600 mb-4">{item.description}</p>
              <p className="text-sm text-gray-500 mb-4">
                {item.expiresAt == null
                  ? 'Prazo: indefinido'
                  : isExpired(item)
                    ? `Prazo encerrado em ${formatExpiry(item.expiresAt)} — arquivada`
                    : `Ativa até ${formatExpiry(item.expiresAt)}`}
              </p>
              {!isCurrentUser && (
                <div className="md:absolute md:bottom-10 md:left-8">
                  <Button text={t('common:actions.send', 'Enviar mensagem')} variant="transparent" onClick={onSendMessage} />
                </div>
              )}
            </div>

            <div className="w-full md:w-2/5 mt-5 md:mt-0">
              {authorInfo && <AuthorDetails authorInfo={authorInfo} title={t('details.aboutAuthor')} />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MuralItemDetailsPopup;
